import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDateForForm, FORM_CONSTANTS } from "../genericInfoFormUtils";
import { isCurrentCulture } from "../../../muiTabs/utils";

export const useGenericInfoForm = ({
  initialData = null,
  isEditMode = false,
  onHasSubAreasChange,
}) => {
  const lastCulture = initialData?.field_colture
    ? initialData?.field_colture[initialData?.field_colture?.length - 1]
    : null;
  const currentCulture = isCurrentCulture(lastCulture) ? lastCulture : null;
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      has_sub_field: initialData?.has_sub_field ?? true,
      name: initialData?.name ?? "",
      field_type: initialData?.field_type || initialData?.field_type?.id || "",
      irrigation_system:
        initialData?.irrigation_system?.id ||
        initialData?.irrigation_system ||
        null,
      field_colture:
        currentCulture?.colture?.id || initialData?.field_colture || null,
    },
  });

  const hasSubAreas = watch("has_sub_field");
  const fieldName = watch("name");
  const fieldType = watch("field_type");
  const irrigationSystem = watch("irrigation_system");
  const fieldCulture = watch("field_colture");

  const [startDate, setStartDate] = useState(
    currentCulture?.start_date
      ? new Date(currentCulture?.start_date)
      : initialData?.start_date
  );
  const [endDate, setEndDate] = useState(
    currentCulture?.end_date
      ? new Date(currentCulture?.end_date)
      : initialData?.end_date
  );
  const [isWarning, setIsWarning] = useState(false);

  // Handlers
  const handleStartDateSelect = useCallback(
    (date) => {
      setStartDate(date);
      setValue("start_date", formatDateForForm(date));
    },
    [setValue]
  );

  const handleEndDateSelect = useCallback(
    (date) => {
      setEndDate(date);
      setValue("end_date", formatDateForForm(date));
    },
    [setValue]
  );

  const handleSubAreasChange = useCallback(
    (event) => {
      const booleanValue = event.target.value === "true";
      setValue("has_sub_field", booleanValue);
      onHasSubAreasChange?.(booleanValue);
    },
    [setValue, onHasSubAreasChange]
  );

  // Validazioni
  const validateStartDate = useCallback(
    (value) => {
      if (fieldCulture && !value) {
        return FORM_CONSTANTS.MESSAGES.START_DATE_REQUIRED;
      }
      return true;
    },
    [fieldCulture]
  );

  const validateEndDate = useCallback(
    (value) => {
      if (fieldCulture && !value) {
        return FORM_CONSTANTS.MESSAGES.END_DATE_REQUIRED;
      }
      if (value && startDate && new Date(value) <= startDate) {
        return FORM_CONSTANTS.MESSAGES.END_DATE_AFTER_START;
      }
      return true;
    },
    [fieldCulture, startDate]
  );

  // Effects
  useEffect(() => {
    register("start_date", { validate: validateStartDate });
    register("end_date", { validate: validateEndDate });
  }, [register, validateStartDate, validateEndDate]);

  useEffect(() => {
    onHasSubAreasChange?.(hasSubAreas);
  }, [hasSubAreas, onHasSubAreasChange]);

  useEffect(() => {
    if (initialData && isEditMode) {
      reset({
        has_sub_field: initialData.has_sub_field ?? true,
        name: initialData.name ?? "",
        field_type: initialData.field_type?.id || initialData?.field_type || "",
        irrigation_system:
          initialData?.irrigation_system?.id ||
          initialData?.irrigation_system ||
          "",
        field_colture:
          currentCulture?.colture?.id || initialData?.field_colture || null,
      });
    }

    if (currentCulture?.start_date || initialData?.start_date) {
      const dataToUse = currentCulture?.start_date || initialData?.start_date;
      const startDateObj = new Date(dataToUse);
      setStartDate(startDateObj);
      setValue("start_date", formatDateForForm(startDateObj));
    } else {
      setStartDate(null);
      setValue("start_date", null);
    }

    if (currentCulture?.end_date || initialData?.end_date) {
      const dataToUse = currentCulture?.end_date || initialData?.end_date;
      const endDateObj = new Date(dataToUse);
      setEndDate(endDateObj);
      setValue("end_date", formatDateForForm(endDateObj));
    } else {
      setEndDate(null);
      setValue("end_date", null);
    }
  }, [
    currentCulture?.start_date,
    currentCulture?.end_date,
    currentCulture?.colture?.id,
    initialData,
    isEditMode,
    reset,
    setValue,
  ]);

  useEffect(() => {
    if (initialData?.has_sub_field && !hasSubAreas && isEditMode) {
      setIsWarning(true);
    } else {
      setIsWarning(false);
    }
  }, [hasSubAreas, initialData?.has_sub_field, isEditMode]);

  return {
    // Form methods (usati nel componente)
    register,
    handleSubmit,
    errors,

    // State
    startDate,
    endDate,
    isWarning,
    fieldName,
    fieldType,
    irrigationSystem,
    fieldCulture,
    hasSubAreas,
    today: useMemo(() => new Date(), []),

    // Handlers
    handlers: {
      handleStartDateSelect,
      handleEndDateSelect,
      handleSubAreasChange,
    },
  };
};
